import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { interval } from 'rxjs';
import { concatMap, filter, retry, switchMap, take, tap } from 'rxjs/operators';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { CompoundResult, GetCompareStatusResponse, GetImportStatusResponse } from '../models/importer-status';
import { DeveloperImportService } from '../services/developer-import.service';
import { GenericModalService } from '../../shared/generic-modal/generic-modal.service';
import { GenericErrorHandlingService } from '../../shared/generic-error-handling/generic-error-handling.service';
import { StateService } from '../../shared/state-management/state.service';

import { DeveloperImportUploadComponent } from './developer-import-upload/developer-import-upload.component';
import { DeveloperImportConfirmComponent } from './developer-import-confirm/developer-import-confirm.component';
import { DeveloperImportStatusComponent } from './developer-import-status/developer-import-status.component';

@Component({
    selector: 'app-import',
    templateUrl: './import.component.html',
    styleUrls: ['./import.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DeveloperImportUploadComponent, DeveloperImportConfirmComponent, DeveloperImportStatusComponent]
})
export class ImportComponent implements OnInit {
    currentPage = 'Upload';
    compareStatus: GetCompareStatusResponse = null;
    versionMismatchDetected = false;
    partnerSearchStateStorageKey = 'partner-search-component-result-state';

    // Modes: partner, distribution, developer
    @Input() importMode: string;

    constructor(
        private importService: DeveloperImportService,
        private loadingPanelService: LoadingPanelService,
        private modalService: GenericModalService,
        private toastService: ToastService,
        private genericErrorHandlingService: GenericErrorHandlingService,
        private stateService: StateService) { }

    ngOnInit(): void {
    }

    async compare(file: File): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Uploading file...');
            this.versionMismatchDetected = false;
            let compareResponse = await this.importService.startCompare(file, false);
            this.loadingPanelService.hideLoadingPanel();

            if (compareResponse.VersionMismatch) {
                await this.modalService.confirmDirect(
                    'Version mismatch detected',
                    'Source system version ' +  (compareResponse.ExportLinkVersion ?? '') + ' does not match target system version ' +  compareResponse.ImportLinkVersion + '.<br><br>OK to continue with the import?',
                    'Yes',
                    'No'
                );

                this.loadingPanelService.showLoadingPanelDirect('Uploading file...');
                this.versionMismatchDetected = true;
                compareResponse = await this.importService.startCompare(file, true);
                this.loadingPanelService.hideLoadingPanel();

                this.waitForCompareStatus(compareResponse.StatusGuid);
            }
            else if (compareResponse.MissingImportPermission) {
                await this.modalService.messageDirect(
                    'Import contains artifacts',
                    'This file contains artifacts but you do not have the required permissions',
                );
            }
            else {
                this.waitForCompareStatus(compareResponse.StatusGuid);
            }
        }
        catch (err) {
            // We get here if the confirm dialog is canceled
            if (err) { console.log(err); }
        }
    }

    waitForCompareStatus(statusGuid: string) {
        this.loadingPanelService.showLoadingPanelDirect('Processing...');
        interval(500)
            .pipe(
                concatMap(() => this.importService.getCompareStatus(statusGuid)),
                retry(2),
                tap({
                    next: value => {
                        if (value.Status.ItemsTotal > 0) {
                            this.loadingPanelService.showLoadingPanelDirect(`Processing item ${value.Status.ItemsDone} of ${value.Status.ItemsTotal}`);
                        }
                    }
                }),
                filter(value => (value?.Status?.Status === 'Done') || (value?.Status?.Status === 'Failed')),
                take(1))
            .subscribe({
                next: value => this.compareStatus = value,
                error: err => {
                    console.log('Failed to upload file', err);
                    this.toastService.showToast('Failed to upload file...', ToastType.error);
                    this.loadingPanelService.hideLoadingPanel();
                },
                complete: async () => {
                    this.loadingPanelService.hideLoadingPanel();
                    switch (this.compareStatus.Status.Status) {
                        case 'Done':
                            this.currentPage = 'Compare';
                            break;
                        default:
                            if (this.versionMismatchDetected) {
                                this.modalService.messageDirect('Import compare error', 'Import failed - possibly due to mismatching versions between source and target systems. <br><br>' + this.compareStatus.Status.Exception)
                            }
                            else{
                                this.genericErrorHandlingService.handleError('ImportCompare', this.compareStatus.Status.Exception, null, 'Import compare error');
                            }
                            break;
                    }
                }
            });
    }

    async import(): Promise<void> {
        try {
            await this.modalService.confirmDirect('Please confirm',
                'Do you want to start the import? Once the import has started, it cannot be canceled.', 'Yes, import it all', 'No, not right now');

            this.loadingPanelService.showLoadingPanelDirect('Importing...');

            await this.startImport(this.compareStatus.Result);
            let importStatus: GetImportStatusResponse = null;
            interval(500)
                .pipe(
                    switchMap(() => this.importService.getImportStatus()),
                    retry(2),
                    tap({
                        next: value => {
                            if (value.Status.ItemsTotal > 0) {
                                this.loadingPanelService.showLoadingPanelDirect(`Importing item ${value.Status.ItemsDone} of ${value.Status.ItemsTotal}`);
                            }
                        }
                    }),
                    filter(value => (value?.Status?.Status === 'Done') || (value?.Status?.Status === 'Failed')),
                    take(1))
                .subscribe({
                    next: value => importStatus = value,
                    error: err => {
                        console.log('Failed to import file', err);
                        this.toastService.showToast('Failed to import file...', ToastType.error);
                        this.loadingPanelService.hideLoadingPanel();
                    },
                    complete: () => {
                        this.loadingPanelService.hideLoadingPanel();
                        switch (importStatus.Status.Status) {
                            case 'Done':
                                if (this.importMode == 'partner') {
                                    let stateObject = this.stateService.getState(this.partnerSearchStateStorageKey);
                                    if (stateObject) {
                                        stateObject.refreshData = true;
                                    }
                                }
                                this.currentPage = 'Status';
                                break;
                            default:
                                if (this.versionMismatchDetected) {
                                    this.modalService.messageDirect('Import error', 'Import failed - possibly due to mismatching versions between source and target systems. <br><br>' + importStatus.Status.Exception)
                                }
                                else{
                                    this.genericErrorHandlingService.handleError('Import', importStatus.Status.Exception, null, 'Import error');
                                }
                                break;
                        }
                    }
                });
        }
        catch (err) {
            // We get here if the confirm dialog is canceled
            if (err) { console.log(err); }
        }
    }

    async startImport(compareResult: CompoundResult) {
        switch (this.importMode) {
            case 'partner':
                await this.importService.startPartnerImport(compareResult);
                break;
            case 'distribution':
                await this.importService.startDistributionImport(compareResult);
                break;
            case 'developer':
                await this.importService.startDeveloperImport(compareResult);
                break;
            case 'transport':
                await this.importService.startTransportImport(compareResult);
                break;
            case 'comparePlugin':
                await this.importService.startComparePluginImport(compareResult);
                break;
            default:
                throw Error('Unknown import mode: ' + this.importMode);
          }
    }

    cancelImport(): void {
        this.compareStatus = null;
        this.currentPage = 'Upload';
    }

    restartImport(): void {
        this.compareStatus = null;
        this.currentPage = 'Upload';
    }
}
